import { useEffect, useState, useRef, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  CheckCircle2, Loader2, ShieldCheck, Send, Euro,
  AlertTriangle, FileUp, Clock, Info,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function fmtDateMaj(iso) {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' });
  } catch (_e) { return null; }
}

/**
 * Page publique remplie par le candidat sous-traitant.
 * 1) Documents légaux (5 docs).
 * 2) Question « Vos tarifs comprennent » (choix unique).
 * 3) Bordereaux tarifaires par corps de métier (uniquement ceux sélectionnés).
 * 4) Tarifs généraux (toujours affichés).
 * Tous les champs tarifaires sont facultatifs. Les valeurs déjà saisies
 * sont pré-remplies (édition ultérieure possible avec le même lien).
 */
export default function ProspectOnboardingPage() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [answersByCode, setAnswersByCode] = useState({});     // { code: '450' }
  const [answersByIdx, setAnswersByIdx] = useState({});       // fallback pour questions sans code
  const [tarifType, setTarifType] = useState('');
  const [uploadedDocs, setUploadedDocs] = useState({});
  const [uploading, setUploading] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const fileInputRefs = useRef({});

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${BACKEND_URL}/api/prospect/onboarding/${token}`);
        setInfo(r.data);
        document.title = `Dossier partenaire — ${r.data.company_name}`;
        setTarifType(r.data.tarif_type_current || '');
        setAnswersByCode(r.data.tarifs_values || {});
        const initialDocs = {};
        (r.data.docs_uploaded || []).forEach((dt) => {
          initialDocs[dt] = { filename: '(déjà déposé)', uploaded_at: new Date().toISOString() };
        });
        setUploadedDocs(initialDocs);
      } catch (e) {
        setError(e?.response?.data?.detail || 'Lien invalide ou expiré');
      } finally { setLoading(false); }
    })();
  }, [token]);

  // Groupe les questions par métier (ordre : métiers → Tarifs généraux)
  const groups = useMemo(() => {
    const g = {};
    const orderedTrades = [];
    (info?.questions || []).forEach((q, i) => {
      const key = q.trade || 'Autres';
      if (!g[key]) { g[key] = []; orderedTrades.push(key); }
      g[key].push({ q, i });
    });
    // Force "Tarifs généraux" tout en bas
    const general = orderedTrades.filter((t) => /tarifs\s*g[ée]n[ée]raux/i.test(t) || t === 'Universel');
    const others = orderedTrades.filter((t) => !general.includes(t));
    return { g, order: [...others, ...general] };
  }, [info?.questions]);

  const setAnswer = (q, idx, value) => {
    if (q.code) setAnswersByCode((p) => ({ ...p, [q.code]: value }));
    else setAnswersByIdx((p) => ({ ...p, [idx]: value }));
  };
  const getAnswer = (q, idx) => {
    if (q.code) return answersByCode[q.code] ?? '';
    return answersByIdx[idx] ?? '';
  };

  const handleFileSelect = async (docType, file) => {
    if (!file) return;
    if (file.size > 15 * 1024 * 1024) {
      toast.error(`${file.name} : trop lourd (max 15 Mo)`); return;
    }
    setUploading(docType);
    try {
      const form = new FormData();
      form.append('doc_type', docType);
      form.append('file', file);
      const r = await axios.post(
        `${BACKEND_URL}/api/prospect/onboarding/${token}/upload`,
        form, { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      setUploadedDocs((p) => ({
        ...p,
        [docType]: { filename: r.data.document.filename, uploaded_at: r.data.document.uploaded_at },
      }));
      toast.success(`${r.data.document.label} déposé`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Échec du dépôt');
    } finally { setUploading(null); }
  };

  const submit = async () => {
    const tarifs = (info?.questions || [])
      .map((q, i) => {
        const raw = getAnswer(q, i);
        if (raw === '' || raw === null || raw === undefined) return null;
        const num = parseFloat(String(raw).replace(',', '.'));
        if (Number.isNaN(num)) return null;
        return {
          code: q.code || null,
          label: q.label,
          unite: q.unite,
          trade: q.trade,
          montant: num,
        };
      })
      .filter(Boolean);
    const uploadedCount = Object.keys(uploadedDocs).length;
    if (tarifs.length === 0 && uploadedCount === 0 && !tarifType) {
      toast.error('Renseignez au moins un tarif, choisissez un type, ou déposez un document');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${BACKEND_URL}/api/prospect/onboarding/${token}/submit`, {
        tarifs,
        tarif_type: tarifType || null,
      });
      setDone(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec de l'envoi");
    } finally { setSubmitting(false); }
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
    </div>
  );
  if (error) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
      <div className="max-w-md bg-white rounded-xl border border-slate-200 shadow-lg p-8 text-center">
        <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-4" />
        <h1 className="text-lg font-bold text-slate-900 mb-2">Lien invalide ou expiré</h1>
        <p className="text-sm text-slate-600">{error}</p>
      </div>
    </div>
  );
  if (done) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
      <div className="max-w-lg bg-white rounded-xl border border-slate-200 shadow-lg p-10 text-center" data-testid="onboarding-thankyou">
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-5">
          <CheckCircle2 className="w-9 h-9 text-green-600" />
        </div>
        <h1 className="text-2xl font-bold text-slate-900 mb-3">Dossier bien reçu !</h1>
        <p className="text-sm text-slate-600 mb-6 leading-relaxed">
          Merci pour votre dossier. Il est maintenant transmis à notre équipe.
        </p>
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-left flex items-start gap-3">
          <Clock className="w-5 h-5 text-amber-700 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-amber-900">
            <b>Prochaines étapes :</b> notre équipe examine votre candidature sous <b>3 à 5 jours ouvrés</b> (vérification des assurances, qualifications, cohérence des tarifs). <b>Ce n'est pas une acceptation automatique.</b> Vous recevrez un email dès que votre référencement sera actif.
          </div>
        </div>
      </div>
    </div>
  );

  const docsRequired = info.docs_required || [];
  const uploadedCount = Object.keys(uploadedDocs).length;
  const dateMaj = fmtDateMaj(info.tarifs_date_maj);

  return (
    <div className="min-h-screen bg-slate-50 py-8 px-4">
      <div className="max-w-2xl mx-auto space-y-6">
        {/* Header */}
        <div className="bg-gradient-to-br from-blue-700 to-blue-900 rounded-xl p-6 text-white shadow-lg">
          <div className="flex items-center gap-3 mb-2">
            <ShieldCheck className="w-8 h-8" />
            <div>
              <h1 className="text-xl font-bold">{info.company_name}</h1>
              <p className="text-sm text-blue-100">Bienvenue dans notre réseau partenaire</p>
            </div>
          </div>
          <p className="text-sm text-blue-100 mt-3">
            Bonjour <b>{info.sous_traitant_nom}</b>, complétez votre dossier ci-dessous.
            Après validation manuelle par notre équipe (3-5 j ouvrés), votre référencement sera actif.
          </p>
          {info.already_submitted && dateMaj && (
            <div className="mt-3 inline-flex items-center gap-2 bg-white/15 rounded-full px-3 py-1 text-xs">
              <Clock className="w-3 h-3" />
              Dossier déjà soumis · Dernière MAJ : {dateMaj}
            </div>
          )}
        </div>

        {/* Section 1 : Documents légaux */}
        <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-bold text-slate-900 flex items-center gap-2">
              <FileUp className="w-4 h-4 text-blue-600" />
              Documents légaux
            </h2>
            <span className="text-xs text-slate-500">{uploadedCount} / {docsRequired.length} déposé(s)</span>
          </div>
          <p className="text-xs text-slate-500 mb-4">
            PDF, JPG, PNG (max 15 Mo par fichier).
          </p>
          <div className="space-y-2">
            {docsRequired.map((d) => {
              const uploaded = uploadedDocs[d.type];
              const isUp = uploading === d.type;
              return (
                <div key={d.type} className="border border-slate-200 rounded-lg p-3 flex items-center gap-3" data-testid={`onboarding-doc-${d.type}`}>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-slate-800">{d.label}</div>
                    <div className="text-xs text-slate-500">{d.hint}</div>
                    {uploaded && (
                      <div className="text-xs text-green-600 mt-1 flex items-center gap-1 truncate">
                        <CheckCircle2 className="w-3 h-3 flex-shrink-0" />
                        <span className="truncate">{uploaded.filename}</span>
                      </div>
                    )}
                  </div>
                  <input
                    type="file"
                    ref={(el) => { fileInputRefs.current[d.type] = el; }}
                    accept=".pdf,.jpg,.jpeg,.png,.webp,.heic,.heif,application/pdf,image/*"
                    className="hidden"
                    onChange={(e) => handleFileSelect(d.type, e.target.files?.[0])}
                    data-testid={`onboarding-doc-input-${d.type}`}
                  />
                  <Button
                    type="button"
                    variant={uploaded ? 'outline' : 'default'}
                    size="sm"
                    disabled={isUp}
                    onClick={() => fileInputRefs.current[d.type]?.click()}
                    className="flex-shrink-0"
                    data-testid={`onboarding-doc-btn-${d.type}`}
                  >
                    {isUp ? (<><Loader2 className="w-4 h-4 mr-1 animate-spin" />Envoi…</>)
                      : uploaded ? (<><FileUp className="w-4 h-4 mr-1" />Remplacer</>)
                      : (<><FileUp className="w-4 h-4 mr-1" />Déposer</>)}
                  </Button>
                </div>
              );
            })}
          </div>
        </div>

        {/* Section 2 : Type de tarif (choix unique) */}
        <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
          <h2 className="font-bold text-slate-900 mb-3 flex items-center gap-2">
            <Euro className="w-4 h-4 text-blue-600" />
            Vos tarifs comprennent généralement :
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            {(info.tarif_types || []).map((t) => (
              <button
                key={t.code}
                type="button"
                onClick={() => setTarifType(t.code)}
                className={`text-left px-3 py-2 rounded-lg border text-sm transition-colors ${tarifType === t.code ? 'border-blue-600 bg-blue-50 text-blue-900 font-semibold' : 'border-slate-200 text-slate-700 hover:border-slate-300'}`}
                data-testid={`onboarding-tariftype-${t.code}`}
              >
                {t.label}
              </button>
            ))}
          </div>

          {/* Note informative */}
          <div className="mt-4 bg-blue-50 border border-blue-100 rounded-lg p-3 flex items-start gap-2">
            <Info className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-blue-900 leading-relaxed">{info.info_tarifs}</p>
          </div>
        </div>

        {/* Bordereaux par métier (uniquement ceux sélectionnés) */}
        {groups.order.map((trade) => {
          const list = groups.g[trade];
          const isGeneral = /tarifs\s*g[ée]n[ée]raux/i.test(trade) || trade === 'Universel';
          return (
            <div key={trade} className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
              <h2 className="font-bold text-slate-900 mb-3 flex items-center gap-2">
                <Euro className={`w-4 h-4 ${isGeneral ? 'text-blue-600' : 'text-slate-500'}`} />
                {isGeneral ? 'Tarifs généraux' : trade}
              </h2>
              <div className="space-y-3">
                {list.map(({ q, i }) => (
                  <div key={q.code || i} className="grid grid-cols-1 sm:grid-cols-[1fr_140px] gap-2 items-center" data-testid={`onboarding-q-${q.code || i}`}>
                    <label className="text-sm text-slate-700">
                      {q.label}
                      <span className="text-xs text-slate-400 ml-2">({q.unite})</span>
                    </label>
                    <Input
                      type="text"
                      inputMode="decimal"
                      placeholder="—"
                      value={getAnswer(q, i)}
                      onChange={(e) => setAnswer(q, i, e.target.value)}
                      className="text-right tabular-nums"
                      data-testid={`onboarding-input-${q.code || i}`}
                    />
                  </div>
                ))}
              </div>
            </div>
          );
        })}

        {/* Info processus */}
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-900 flex items-start gap-3">
          <Clock className="w-5 h-5 flex-shrink-0 mt-0.5" />
          <div>
            <b>Après envoi :</b> votre dossier sera examiné par notre équipe sous <b>3 à 5 jours ouvrés</b>.
            Ce n'est pas une acceptation automatique.
          </div>
        </div>

        {/* CTA */}
        <Button
          onClick={submit}
          disabled={submitting}
          className="w-full bg-blue-600 hover:bg-blue-700 py-6 text-base"
          data-testid="onboarding-submit-btn"
        >
          {submitting ? <><Loader2 className="w-5 h-5 mr-2 animate-spin" />Envoi…</> : <><Send className="w-5 h-5 mr-2" />Envoyer mon dossier</>}
        </Button>
      </div>
    </div>
  );
}
